import { validateBetaInviteCodePublic } from "@/api/publicBetaInvite";
import { Button } from "@/components/ui/button";
import {
  PENDING_BETA_INVITE_CODE_KEY,
  PENDING_BETA_INVITE_EMAIL_KEY,
  sanitizeBetaInviteEmail,
} from "@/constants/betaInvite";
import { ROUTES } from "@/constants/routes";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { useAuth0 } from "@auth0/auth0-react";
import { Spinner } from "@cohesive/ui-components";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CenteredLayout } from "@/layouts/CenteredLayout";

export function BetaInviteContinuePage() {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [codeRevalidated, setCodeRevalidated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const raw =
        typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem(PENDING_BETA_INVITE_CODE_KEY)
          : null;
      const code = raw?.trim() ?? "";

      if (!code) {
        navigate(ROUTES.INVITE, { replace: true });
        return;
      }

      try {
        await validateBetaInviteCodePublic(code);
        if (!cancelled) {
          setCodeRevalidated(true);
        }
      } catch {
        if (typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(PENDING_BETA_INVITE_CODE_KEY);
          sessionStorage.removeItem(PENDING_BETA_INVITE_EMAIL_KEY);
        }
        if (!cancelled) {
          navigate(ROUTES.INVITE, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!codeRevalidated || isLoading) {
      return;
    }
    if (isAuthenticated) {
      navigate(ROUTES.ONBOARDING.CREATE_ORGANIZATION, { replace: true });
    }
  }, [codeRevalidated, isAuthenticated, isLoading, navigate]);

  const handleCreateAccount = () => {
    // Re-sanitized on read so stale or hand-edited storage can't ride along.
    const email = sanitizeBetaInviteEmail(
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(PENDING_BETA_INVITE_EMAIL_KEY)
        : null,
    );
    void loginWithRedirect({
      authorizationParams: {
        screen_hint: "signup",
        login_hint: email ?? undefined,
      },
      appState: {
        returnTo: ROUTES.ONBOARDING.CREATE_ORGANIZATION,
      },
    });
  };

  if (!codeRevalidated || isLoading || isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-dvh">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      <title>Beta access confirmed</title>
      <CenteredLayout maxWidth="md">
        <div className="space-y-8 text-center">
          <div className="space-y-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="flex justify-center"
            >
              <LogoIcon width="64" height="64" />
            </motion.div>

            <motion.h1
              className="text-3xl sm:text-5xl font-bold text-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
            >
              You are in
            </motion.h1>

            <motion.p
              className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto font-medium tracking-tight px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
            >
              Your beta code is valid.
            </motion.p>

            <motion.p
              className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto font-medium tracking-tight px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
            >
              Continue to create your account and set up your organization.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.35, ease: "easeOut" }}
          >
            <Button type="button" size="lg" onClick={handleCreateAccount}>
              Create your account
            </Button>
          </motion.div>
        </div>
      </CenteredLayout>
    </>
  );
}
